import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Callout } from "@/components/ui/feedback";
import { CopyButton } from "@/components/ui/misc";
import { apiUrl } from "@/lib/api";
import type { Trigger } from "@/lib/types";
import { exampleBody, formFieldNames } from "@/lib/triggers";

/**
 * How to call a trigger (ADR-021): the URL, the header, a body its mapping reads, and the steps in a
 * zap. The body is built from the trigger's own mapping, so it is always one the trigger can apply;
 * the secret is never here, because core-api cannot read it back.
 */
export function TriggerSetupCard({ trigger }: { trigger: Trigger }) {
  const url = apiUrl(trigger.path);
  const body = exampleBody(trigger);
  const names = formFieldNames(trigger);
  const json = JSON.stringify(body, null, 2);
  const curl = [
    `curl -X POST '${url}' \\`,
    `  -H 'Content-Type: application/json' \\`,
    `  -H '${trigger.secret_header}: <the secret>' \\`,
    `  -d '${JSON.stringify(body)}'`,
  ].join("\n");

  return (
    <Card className="overflow-hidden">
      <CardHeader title="How to call it" description="Where a sender posts, what it sends, and how to set a zap up." />
      <CardBody className="space-y-4">
        <dl className="grid grid-cols-[6rem_1fr] items-center gap-x-3 gap-y-2 text-xs">
          <dt className="text-text-muted">URL</dt>
          <dd className="flex min-w-0 items-center gap-1 font-mono text-text">
            <span className="break-all">{url}</span>
            <CopyButton value={url} label="Copy the URL" />
          </dd>
          <dt className="text-text-muted">Method</dt>
          <dd className="font-mono text-text">POST</dd>
          <dt className="text-text-muted">Header</dt>
          <dd className="flex items-center gap-1 text-text">
            <span className="font-mono">{trigger.secret_header}</span>
            <CopyButton value={trigger.secret_header} label="Copy the header name" />
            <span className="text-text-muted">with the secret shown when the trigger was made, or last rotated</span>
          </dd>
        </dl>

        {trigger.action === "capture" && (
          <Callout tone="info">
            This trigger only keeps what arrives. Send it a request, open the request below to see its fields, then
            change the trigger's action and map them.
          </Callout>
        )}

        <div className="space-y-1.5">
          <p className="text-xs font-medium text-text">From a zap</p>
          <ol className="list-decimal space-y-1 pl-5 text-xs text-text-muted">
            <li>Add an action step: Webhooks by Zapier, then POST.</li>
            <li>URL: the URL above.</li>
            <li>
              {names
                ? `Payload type: Form or JSON. Data: one field for each of ${names.join(", ")}, each filled from an earlier step.`
                : "Payload type: JSON, with Unflatten on. Data: a field for each path below, with two underscores where the path has a dot (contact__phone for contact.phone)."}
            </li>
            <li>Headers: {trigger.secret_header}, with the secret as its value.</li>
            <li>Test the step. The request shows up under Requests below, with what it made.</li>
          </ol>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-text">A body it reads</p>
            <CopyButton value={json} label="Copy the body" />
          </div>
          <pre className="max-h-60 overflow-auto rounded-md border border-border bg-surface-sunken px-3 py-2 font-mono text-2xs text-text">
            {Object.keys(body).length > 0 ? json : "Any JSON object, or any form."}
          </pre>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-text">From a terminal</p>
            <CopyButton value={curl} label="Copy the command" />
          </div>
          <pre className="overflow-x-auto rounded-md border border-border bg-surface-sunken px-3 py-2 font-mono text-2xs text-text">
            {curl}
          </pre>
        </div>
      </CardBody>
    </Card>
  );
}
